// Animal system: species, placement in enclosures, needs, wellbeing, wandering, reproduction.
// Placeholder low-poly models built from primitives — swapped for user-supplied FBX later.
import * as THREE from 'three';
import { store, pushNews } from '../core/store.js';
import { bus } from '../core/bus.js';
import { SPECIES } from './animalCatalog.js';
import {
  BREED_GROUPS, BREED_OF, CONTRACT_ZOOS, FIXED_SEX, MANDATE_REHOME, MORPHS, PREDATOR, STAR_NAMES,
  bloodlineBirth, bloodlineFactor, bloodlineFresh, bloodlinePct, bloodlineReset, canShare, groupOf,
  rollMorph, saleValue,
} from './animalRules.js';
import { animalImpostor, buildLOD, buildModel, hasRealAnimalModel } from './animalModels.js';
import { disposeBadge, getEmotes, makeHerdBadge, tintMorph } from './animalRenderHelpers.js';
import { hasPerk } from './gameplay.js';
export { SPECIES } from './animalCatalog.js';
export { ANIMAL_THUMBS } from './animalModels.js';
export {
  MORPHS,
  bloodlineBirth, bloodlineFactor, bloodlineFresh, bloodlinePct, bloodlineReset,
  canShare, rollMorph, saleValue,
} from './animalRules.js';

let nextId = 1;

export class AnimalSystem {
  constructor(world, build) {
    this.world = world;
    this.build = build;
    this.animals = [];
    this.group = new THREE.Group();
    world.renderer.scene.add(this.group);
    {
      const em = getEmotes();
      const r = world.renderer;
      [em.happy, em.sad].forEach((m) => r.renderer.initTexture(m.map));
      const w1 = new THREE.Sprite(em.happy), w2 = new THREE.Sprite(em.sad);
      this.group.add(w1, w2);
      r.renderer.compile(r.scene, r.camera);
      this.group.remove(w1, w2);
    }

    // Second CPU core: daily animal sim runs in a worker (see js/core/sim-worker.js)
    try {
      this._worker = new Worker(new URL('../core/sim-worker.js', import.meta.url));
      this._worker.onmessage = (e) => { if (e.data.type === 'animalsDaily') this._applyResults(e.data); };
    } catch { this._worker = null; }


    this._subs = [
      bus.on('build:animalPlace', ({ species, cx, cz }) => this.tryPlace(species, cx, cz)),
      bus.on('sim:newDay', () => this.dailyTick()),
      bus.on('cull:choose', ({ species, keepM, keepF }) => this.chooseCull(species, keepM, keepF)),
      bus.on('cull:decline', (sp) => this.declineCull(sp)),
      bus.on('mandate:trim', (sp) => this.mandateTrim(sp)),
      bus.on('mandate:recheck', () => this._mandateCheck()),
      bus.on('contract:accept', () => this.acceptContract()),
      bus.on('contract:decline', () => { this._contract = null; store.set({ contractOffer: null }); }),
      bus.on('cull:buyblood', (sp) => this.buyNewBlood(sp)),
      bus.on('animal:release', (sp) => this.releaseToWild(sp)),
      bus.on('animals:treatEnc', (id) => this.treatEnclosure(id)),
      bus.on('animal:sell', (id) => {
        const an = this.animals.find((x) => x.id === id);
        if (!an) return;
        const value = saleValue(an);
        const sp = SPECIES[an.species];
        this.group.remove(an.model);
        this.animals.splice(this.animals.indexOf(an), 1);
        store.set({ money: store.get().money + value, visitorCount: store.get().visitorCount });
        this._reflowRender();
        pushNews(`${an.name || 'A ' + sp.label.toLowerCase()} moved to another zoo for $${value.toLocaleString('en-US')}.`, 'info');
        bus.emit('ui:toast', { text: `🚚 Sold for $${value.toLocaleString('en-US')}!`, kind: 'success' });
      }),
      bus.on('animal:rename', ({ id, name }) => {
        const an = this.animals.find((x) => x.id === id);
        if (an) { an.name = (name || '').slice(0, 20); bus.emit('ui:toast', { text: an.name ? `Say hi to ${an.name}!` : 'Name removed.', kind: 'success' }); }
      }),
      // Hot-swap procedural placeholders once a real model finishes loading (fixes the load race).
      bus.on('animals:modelReady', (key) => {
        for (const a of this.animals) {
          if (a.species !== key || a.usesReal) continue;
          a.usesReal = true;
          if (!a.model) continue;
          const nm = buildModel(key);
          nm.position.copy(a.model.position); nm.rotation.copy(a.model.rotation); nm.scale.copy(a.model.scale);
          const em = new THREE.Sprite(getEmotes().happy); em.scale.set(1.4, 1.4, 1); em.position.y = 3.2; em.visible = false; nm.add(em);
          this.group.remove(a.model); this.group.add(nm); a.model = nm; a.emote = em;
        }
      }),
    ];
    // Petting: click an animal (no tool active) → it hops and shows a heart. Pure joy.
    this._raycaster = new THREE.Raycaster();
    this._onPointerDown = (e) => {
      if (e.button !== 0 || store.get().activeTool) return;
      const dom = world.renderer.renderer.domElement;
      const rect = dom.getBoundingClientRect();
      const p = new THREE.Vector2(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
      this._raycaster.setFromCamera(p, world.renderer.camera);
      const hit = this._raycaster.intersectObjects(this.group.children, true)[0];
      if (!hit) return;
      let root = hit.object;
      while (root.parent && root.parent !== this.group) root = root.parent;
      const a = this.animals.find((an) => an.model === root);
      if (a && a.hopT == null) {
        a.hopT = 0;
        if (a.emote) { a.emote.material = getEmotes().happy; a.emote.visible = true; a.emoteTimer = 1.6; }
        bus.emit('animal:selected', { id: a.id, species: a.species, label: SPECIES[a.species].label, name: a.name || '', value: saleValue(a), baby: a.maturity < 1, ageDays: a.ageDays || 0 });
      }
    };
    world.renderer.renderer.domElement.addEventListener('pointerdown', this._onPointerDown);
  }
  dispose() {
    this._subs.forEach((u) => u());
    this.world.renderer.renderer.domElement.removeEventListener('pointerdown', this._onPointerDown);
    this._worker?.terminate?.();
    this.world.renderer.scene.remove(this.group);
  }

  // Government mandate counts ADULTS only (maturity >= 1); young are free until they grow up.
  adultCount() { let n = 0; for (const a of this.animals) if (a.maturity >= 1 && !a.escaped) n++; return n; }

  // Over the mandate? Surface a forced-reduction popup (pick which species to reduce to youngsters).
  _mandateCheck() {
    const cap = store.get().animalCap;
    if (cap == null) { if (store.get().mandateOffer) store.set({ mandateOffer: null }); return; }
    const bySp = {}; let total = 0;
    for (const a of this.animals) {
      if (a.escaped || a.maturity < 1) continue;
      total++; (bySp[a.species] = bySp[a.species] || []).push(a);
    }
    const over = total - cap;
    if (over <= 0) { if (store.get().mandateOffer) store.set({ mandateOffer: null }); return; }
    const species = Object.keys(bySp).map((sp) => {
      const young = this.animals.filter((a) => a.species === sp && a.maturity < 1 && !a.escaped).length;
      const earn = Math.round(bySp[sp].reduce((s, a) => s + saleValue(a), 0) * MANDATE_REHOME);
      const purity = bloodlinePct(sp);
      return { key: sp, label: SPECIES[sp].label, adults: bySp[sp].length, young, earn, purity, canRelease: purity >= 70 };
    }).sort((a, b) => b.adults - a.adults);
    store.set({ mandateOffer: { over, cap, total, species } });
  }

  // Reduce a species to only its youngsters — sell ALL its adults, keep every juvenile.
  mandateTrim(sp) {
    const adults = this.animals.filter((a) => a.species === sp && a.maturity >= 1 && !a.escaped);
    if (!adults.length) return;
    let earned = 0;
    for (const a of adults) { earned += saleValue(a) * MANDATE_REHOME; if (a.model) this.group.remove(a.model); const i = this.animals.indexOf(a); if (i >= 0) this.animals.splice(i, 1); }
    earned = Math.round(earned);
    if (earned) store.set({ money: store.get().money + earned });
    this._reflowRender();
    pushNews(`Government mandate: ${SPECIES[sp].label} reduced to youngsters only — ${adults.length} adult${adults.length > 1 ? 's' : ''} rehomed for $${earned.toLocaleString('en-US')}.`, 'info');
    bus.emit('animals:changed', this.animals.length);
    this._mandateCheck();
  }

  // CONSERVATION: release a species' adults to the wild. Only worthwhile for PURE bloodlines
  // (≥70%): pays a conservation grant + prestige + reputation (not below-market cash), keeps young,
  // and refreshes the studbook. A positive, rewarding outlet for over-cap pressure.
  releaseToWild(sp) {
    const purity = bloodlinePct(sp);
    const adults = this.animals.filter((a) => a.species === sp && a.maturity >= 1 && !a.escaped);
    if (!adults.length) return;
    const def = SPECIES[sp];
    let grant = 0;
    for (const a of adults) { grant += def.price * 0.4 * (purity / 100); if (a.model) this.group.remove(a.model); const i = this.animals.indexOf(a); if (i >= 0) this.animals.splice(i, 1); }
    grant = Math.round(grant);
    const prestige = Math.max(1, Math.round(adults.length * (purity / 100) * 0.6));
    const s = store.get();
    store.set({ money: s.money + grant, prestige: (s.prestige || 0) + prestige, reputation: Math.min(100, (s.reputation || 50) + 2), released: (s.released || 0) + adults.length });
    bus.emit('economy:track', { cat: 'grants', amt: grant });
    bloodlineFresh(sp);
    this._reflowRender();
    pushNews(`\uD83D\uDD4A\uFE0F Released ${adults.length} ${def.label}${adults.length > 1 ? 's' : ''} to the wild \u2014 conservation grant $${grant.toLocaleString('en-US')}, +${prestige} prestige.`, 'success');
    bus.emit('animals:changed', this.animals.length);
    { const _ad = this.adultCount(); if (store.get().adultCount !== _ad) store.set({ adultCount: _ad }); }
    this._mandateCheck();
  }

  // STAR ATTRACTIONS: rare species (minPrestige≥15) + morphs draw crowds. Drives a daily reputation lift.
  starPower() {
    let morphs = 0; const rare = new Set();
    for (const a of this.animals) { if (a.escaped) continue; if (a.morph) morphs++; if (SPECIES[a.species] && SPECIES[a.species].minPrestige >= 15) rare.add(a.species); }
    return { morphs, rare: rare.size, score: morphs * 3 + rare.size * 2 };
  }

  // Pre-bake billboard impostors for the species already in the zoo, spread over idle time, so the
  // first far view of a species doesn't hitch (the render-to-texture bake is done ahead of time).
  prewarmImpostors() {
    const species = [...new Set(this.animals.map((a) => a.species))];
    let i = 0;
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 80));
    const step = () => { if (i >= species.length) return; try { animalImpostor(species[i]); } catch (e) {} i++; idle(step); };
    idle(step);
  }

  // Named star roster for the Visitors panel (morphs + rare species that carry a name).
  starList() {
    const out = [];
    for (const a of this.animals) {
      if (a.escaped || !a.star || !a.name) continue;
      out.push({ name: a.name, label: SPECIES[a.species] ? SPECIES[a.species].label : a.species, morph: a.morph && MORPHS[a.morph] ? MORPHS[a.morph].label : null });
    }
    return out.slice(0, 12);
  }

  // Total sick animals across the zoo (drives the topbar alert). Cheap; recounted when results settle.
  _recountSick() {
    let n = 0; for (const a of this.animals) if (a.sick && !a.escaped) n++;
    if (store.get().sickTotal !== n) store.set({ sickTotal: n });
  }

  // Dispatch a vet team to instantly treat every sick animal in one enclosure, for a per-head fee.
  treatEnclosure(id) {
    const sick = this.animals.filter((a) => a.enclosureId === id && a.sick && !a.escaped);
    if (!sick.length) return;
    let cost = sick.length * (store.get().kidsMode ? 130 : 260);
    const s = store.get();
    if (s.money < cost) { bus.emit('ui:toast', { text: 'Not enough cash for the vet team!', kind: 'error' }); return; }
    store.set({ money: Math.round(s.money - cost) });
    for (const a of sick) { a.sick = 0; if (a.emote) a.emote.visible = false; }
    this._recountSick();
    bus.emit('animals:changed', this.animals.length);
    pushNews(`\uD83D\uDE91 Vet team treated ${sick.length} sick animal${sick.length > 1 ? 's' : ''} \u2014 $${cost.toLocaleString('en-US')}.`, 'success');
  }

  // Per-habitat summary for the Habitat panel (species, wellbeing, space/social, disease, purity, stars).
  enclosureInfo(id) {
    const enc = this.build.enclosures.find((e) => e.id === id); if (!enc) return null;
    const here = this.animals.filter((a) => a.enclosureId === id && !a.escaped);
    const bySp = {};
    for (const a of here) { const o = bySp[a.species] || (bySp[a.species] = { n: 0, young: 0, well: 0, sick: 0, morphs: 0 }); o.n++; if (a.maturity < 1) o.young++; o.well += (a.wellbeing || 0); if (a.sick) o.sick++; if (a.morph) o.morphs++; }
    const species = Object.keys(bySp).map((sp) => ({ key: sp, label: SPECIES[sp].label, n: bySp[sp].n, young: bySp[sp].young, well: Math.round(bySp[sp].well / bySp[sp].n), sick: bySp[sp].sick, morphs: bySp[sp].morphs, purity: bloodlinePct(sp), spaceNeed: SPECIES[sp].spacePerAnimal, socialMin: SPECIES[sp].socialMin, rare: SPECIES[sp].minPrestige >= 15, canRelease: bloodlinePct(sp) >= 70 && bySp[sp].n - bySp[sp].young > 0 }));
    const spaceNeed = here.reduce((s, a) => s + (SPECIES[a.species] ? SPECIES[a.species].spacePerAnimal : 0), 0);
    return {
      id, size: enc.cells.size, count: here.length,
      avgWell: here.length ? Math.round(here.reduce((s, a) => s + (a.wellbeing || 0), 0) / here.length) : 0,
      sick: here.reduce((n, a) => n + (a.sick ? 1 : 0), 0),
      spaceNeed, spaceOk: enc.cells.size >= spaceNeed,
      species,
    };
  }

  enclosureAt(cx, cz) {
    return this.build.enclosures.find((e) => e.cells.has(`${cx},${cz}`));
  }

  tryPlace(species, cx, cz) {
    const sp = SPECIES[species];
    if (store.get().prestige < sp.minPrestige) { bus.emit('ui:toast', { text: `Locked — reach ${sp.minPrestige} prestige to unlock the ${sp.label.toLowerCase()}!`, kind: 'error' }); return; }
    const enc = this.enclosureAt(cx, cz);
    if (!enc) { bus.emit('ui:toast', { text: 'Animals must be placed inside a fenced enclosure.', kind: 'error' }); return; }
    // Compatibility: no lions with zebras...
    const resident = this.animals.find((a) => a.enclosureId === enc.id && !canShare(species, a.species));
    if (resident) {
      const other = SPECIES[resident.species];
      bus.emit('ui:toast', { text: `${sp.label}s can't live with ${other.label.toLowerCase()}s — build them their own habitat!`, kind: 'error' });
      return;
    }
    const cap = store.get().animalCap;
    if (cap != null && this.adultCount() >= cap) {
      bus.emit('ui:toast', { text: `Animal permit full (${this.adultCount()}/${cap} adults) — raise your government permit in the Genetics Lab.`, kind: 'error' });
      return;
    }
    const s = store.get();
    if (s.money < sp.price) { bus.emit('ui:toast', { text: 'Not enough cash!', kind: 'error' }); return; }
    store.set({ money: s.money - sp.price });
    this.spawn(species, enc, cx, cz, 1);
    bloodlineFresh(species);
    pushNews(`${sp.label} arrived at ${s.zooName}!`, 'success');
    store.set({ prestige: store.get().prestige + 3 });
  }

  spawn(species, enclosure, cx, cz, maturity = 1, morph = null) {
    const model = buildModel(species);
    tintMorph(model, morph);
    model.traverse((o) => { if (o.isMesh) o.castShadow = false; }); // perf: animals don't cast shadows (hundreds of them)
    const t = this.world.terrain;
    const half = t.worldSize / 2;
    const wx = (cx + 0.5) * t.cellSize - half;
    const wz = (cz + 0.5) * t.cellSize - half;
    model.position.set(wx, t.heightAtWorld(wx, wz), wz);
    this.group.add(model);
    const _star = !!morph || (SPECIES[species] && SPECIES[species].minPrestige >= 15);
    const animal = {
      id: nextId++,
      species,
      name: _star ? STAR_NAMES[(Math.random() * STAR_NAMES.length) | 0] : '',
      star: !!_star,
      sex: FIXED_SEX[species] || ((this.animals.filter((a) => a.species === species && a.enclosureId === enclosure.id).length % 2 === 0) ? 'm' : 'f'),
      enclosureId: enclosure.id,
      model,
      morph: morph || null,
      maturity,                    // 0..1, grows
      ageDays: maturity >= 1 ? 30 : 0, // bought adults arrive ~30 days old
      wellbeing: 70,
      emote: (() => {
        const s = new THREE.Sprite(getEmotes().happy);
        s.scale.set(1.4, 1.4, 1); s.position.y = 3.2; s.visible = false; model.add(s); return s;
      })(),
      pregnantDays: null,
      target: null,
      speed: 1.2 + Math.random() * 0.8,
      spawnCell: { cx, cz },
    };
    model.scale.setScalar(0.35 + maturity * 0.65);
    this.animals.push(animal);
    animal.usesReal = hasRealAnimalModel(species);
    { const _ad = this.adultCount(); if (store.get().adultCount !== _ad) store.set({ adultCount: _ad }); }
    return animal;
  }

  // Per-enclosure stress penalty: prey in/next to a predator enclosure, unless a privacy hedge shelters it.
  _predatorStress() {
    const encs = this.build.enclosures;
    if (!encs || !encs.length) return {};
    const sppByEnc = new Map();
    for (const a of this.animals) {
      if (a.escaped) continue;
      let s = sppByEnc.get(a.enclosureId); if (!s) sppByEnc.set(a.enclosureId, s = new Set());
      s.add(a.species);
    }
    const danger = new Set(); const R = 2;
    for (const e of encs) {
      const spp = sppByEnc.get(e.id); if (!spp) continue;
      let hasPred = false; for (const sp of spp) if (PREDATOR.has(sp)) { hasPred = true; break; }
      if (!hasPred) continue;
      for (const key of e.cells) {
        const [cx, cz] = key.split(',').map(Number);
        for (let dz = -R; dz <= R; dz++) for (let dx = -R; dx <= R; dx++) danger.add((cx + dx) + ',' + (cz + dz));
      }
    }
    if (!danger.size) return {};
    const out = {};
    for (const e of encs) {
      const spp = sppByEnc.get(e.id); if (!spp) continue;
      let onlyPred = true; for (const sp of spp) if (!PREDATOR.has(sp)) { onlyPred = false; break; }
      if (onlyPred) continue;
      let near = false; for (const key of e.cells) if (danger.has(key)) { near = true; break; }
      if (!near) continue;
      const sheltered = this.world.props && this.world.props.hasShelter && this.world.props.hasShelter(e.cells);
      out[e.id] = sheltered ? 10 : 28;
    }
    return out;
  }

  // Vet coverage per enclosure (0..1) from vet-clinic amenities within range.
  _vetCoverage() {
    const out = {};
    if (!this.world.amenities) return out;
    const vets = this.world.amenities.items.filter((it) => it.type === 'vet' && it.model);
    if (!vets.length) return out;
    const t = this.world.terrain, half = t.worldSize / 2;
    for (const e of this.build.enclosures) {
      if (!e.cells.size) continue;
      const c = this._encCenter(e.id);
      let best = 0;
      for (const v of vets) {
        const d = v.model.position.distanceTo(c);
        best = Math.max(best, Math.max(0, 1 - d / 60)); // full within ~0m, fades to 0 at 60m
      }
      if (best > 0) out[e.id] = best;
    }
    return out;
  }

  _encCenter(encId) {
    const enc = this.build.enclosures.find((e) => e.id === encId);
    const t = this.world.terrain, half = t.worldSize / 2;
    if (!enc || !enc.cells.size) return new THREE.Vector3(0, 0, 0);
    const [cx, cz] = [...enc.cells][0].split(',').map(Number);
    return new THREE.Vector3((cx + 0.5) * t.cellSize - half, 0, (cz + 0.5) * t.cellSize - half);
  }
  _spawnPos(a) {
    const t = this.world.terrain, half = t.worldSize / 2;
    if (a.spawnCell) return new THREE.Vector3((a.spawnCell.cx + 0.5) * t.cellSize - half, 0, (a.spawnCell.cz + 0.5) * t.cellSize - half);
    return this._encCenter(a.enclosureId);
  }

  _ensureModel(a) {
    if (a.model) return;
    const t = this.world.terrain;
    const m = buildModel(a.species);
    tintMorph(m, a.morph);
    m.traverse((o) => { if (o.isMesh) o.castShadow = false; }); // perf: animals don't cast shadows
    const p = this._spawnPos(a); p.y = t.heightAtWorld(p.x, p.z);
    m.position.copy(p);
    m.scale.setScalar(0.35 + (a.maturity != null ? a.maturity : 1) * 0.65);
    const em = new THREE.Sprite(getEmotes().happy); em.scale.set(1.4, 1.4, 1); em.position.y = 3.2; em.visible = false; m.add(em);
    this.group.add(m); a.model = m; a.emote = em; a.lod = 'hi';
  }
  // Swap an animal between detailed (hi) and low-poly proxy (lo) for distance LOD.
  _setLOD(a, lod) {
    if ((a.lod || 'hi') === lod || !a.model) return;
    const pos = a.model.position.clone(), rotY = a.model.rotation.y, vis = a.model.visible;
    pos.y = this.world.terrain.heightAtWorld(pos.x, pos.z); // ALWAYS re-ground — never inherit a prior LOD's Y (the bill sprite adds h*0.5); that leak floated stationary animals
    const badge = a.badge; if (badge) a.model.remove(badge);
    this.group.remove(a.model);
    const mat = 0.35 + (a.maturity != null ? a.maturity : 1) * 0.65;
    let m, em;
    if (lod === 'bill') {
      // Camera-facing billboard (Sprite) using the species impostor — auto-rotates with the camera.
      const imp = animalImpostor(a.species);
      m = new THREE.Sprite(new THREE.SpriteMaterial({ map: imp.tex, transparent: true, depthWrite: true }));
      const h = Math.max(1, imp.h * mat); m.scale.set(h * (imp.w / imp.h), h, 1);
      m.position.copy(pos); m.position.y += h * 0.5;
      em = new THREE.Sprite(getEmotes().happy); em.scale.set(1.4, 1.4, 1); em.position.y = h; em.visible = false; m.add(em);
    } else {
      m = buildLOD(a.species, lod);
      tintMorph(m, a.morph);
      m.traverse((o) => { if (o.isMesh) o.castShadow = false; });
      m.position.copy(pos); m.rotation.y = rotY;
      m.scale.setScalar(mat);
      em = new THREE.Sprite(getEmotes().happy); em.scale.set(1.4, 1.4, 1); em.position.y = 3.2; em.visible = false; m.add(em);
      if (badge) m.add(badge);
    }
    m.visible = vis;
    this.group.add(m); a.model = m; a.emote = em; a.lod = lod;
  }
  _removeModel(a) {
    if (a.badge) { disposeBadge(a.badge); a.badge = null; }
    if (a.model) { this.group.remove(a.model); a.model = null; }
    a.emote = null;
  }

  // Rendering is a READ-ONLY projection of the sim (no animal data is ever removed).
  // Per enclosure+species+sex: young show 1 figure, adults show up to CAP figures; the rest are
  // hidden and a ×N badge shows the true herd size. Morphs & escaped animals always render individually.
  _reflowRender() {
    // Coalesce: many day-roll paths (births, deaths, culls, mandate, contracts) call this within one
    // tick — collapse them into a SINGLE rebuild on the next frame so the day-roll never hitches.
    if (this._reflowScheduled) return;
    this._reflowScheduled = true;
    requestAnimationFrame(() => { this._reflowScheduled = false; if (this.world && this.world.renderer) this._reflowNow(); });
  }
  _reflowNow() {
    const CAP_ADULT = 8;
    const buckets = new Map();
    for (const a of this.animals) {
      if (a.escaped || a.morph) {
        this._ensureModel(a);
        if (a.badge) { a.model.remove(a.badge); disposeBadge(a.badge); a.badge = null; }
        a.model.visible = true;
        continue;
      }
      const age = a.maturity < 1 ? 'y' : 'a';
      const key = a.enclosureId + ':' + a.species + ':' + a.sex + ':' + age;
      let l = buckets.get(key); if (!l) buckets.set(key, l = []); l.push(a);
    }
    for (const [key, list] of buckets) {
      const show = Math.min(key.endsWith(':y') ? 1 : CAP_ADULT, list.length);
      for (let i = 0; i < list.length; i++) {
        if (i < show) {
          this._ensureModel(list[i]);
          if (list[i].badge) { list[i].model.remove(list[i].badge); disposeBadge(list[i].badge); list[i].badge = null; }
          list[i].model.visible = true;
        } else {
          this._removeModel(list[i]);
        }
      }
      if (list.length > show) {
        const rep = list[0];
        if (rep.badge) { rep.model.remove(rep.badge); disposeBadge(rep.badge); rep.badge = null; }
        rep.badge = makeHerdBadge(list.length);
        rep.model.add(rep.badge);
      }
    }
    const _ad = this.adultCount();
    if (store.get().adultCount !== _ad) store.set({ adultCount: _ad });
  }

  // Shared instanced blob shadows: one soft disc under every rendered animal, drawn in a SINGLE draw
  // call (no shadow-map cost). Restores grounding now that animals/trees don't cast real shadows.
  _initBlobs() {
    const c = document.createElement('canvas'); c.width = c.height = 64;
    const x = c.getContext('2d');
    const g = x.createRadialGradient(32, 32, 2, 32, 32, 32);
    g.addColorStop(0, 'rgba(0,0,0,0.4)'); g.addColorStop(0.6, 'rgba(0,0,0,0.16)'); g.addColorStop(1, 'rgba(0,0,0,0)');
    x.fillStyle = g; x.beginPath(); x.arc(32, 32, 32, 0, 7); x.fill();
    const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace;
    const geo = new THREE.PlaneGeometry(1, 1); geo.rotateX(-Math.PI / 2);
    const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false, opacity: 0.85 });
    const im = new THREE.InstancedMesh(geo, mat, 320);
    im.frustumCulled = false; im.renderOrder = 2; im.count = 0;
    this.group.add(im); this._blobs = im;
    this._blobM = new THREE.Matrix4(); this._blobQ = new THREE.Quaternion(); this._blobSc = new THREE.Vector3(); this._blobP = new THREE.Vector3();
  }

  // Called every render frame for smooth movement.
  update(dt) {
    const t = this.world.terrain;
    const half = t.worldSize / 2;
    // Distance LOD: far animals use the cheap low-poly proxy; near ones the detailed model.
    this._lodFrame = (this._lodFrame || 0) + 1;
    if (this._lodFrame % 15 === 0) {
      const cam = this.world.renderer.camera;
      const A = this.animals, N = A.length;
      if (N) {
        // Amortize LOD swaps: at most SWAP_BUDGET mesh rebuilds per pass, resuming from a cursor next
        // time. Scanning (cheap distance test) still covers everyone; only the costly _setLOD is capped —
        // this kills the pan-time 1%-low spike from dozens of animals crossing a threshold in one frame.
        const SWAP_BUDGET = 6;
        let budget = SWAP_BUDGET, scanned = 0, i = this._lodCursor || 0;
        while (scanned < N && budget > 0) {
          const a = A[i % N]; i++; scanned++;
          if (!a || !a.model || !a.model.visible || a.morph) continue;
          const d = cam.position.distanceTo(a.model.position);
          const want = d < 120 ? 'hi' : d < 190 ? 'lo' : 'bill';
          if (want !== (a.lod || 'hi')) { this._setLOD(a, want); budget--; }
        }
        this._lodCursor = i % N;
        // Aerial-perspective fade: distant billboards wash toward the sky haze + go slightly translucent
        // so the hi→billboard swap and the far edge are far less noticeable. Cheap — only 'bill' sprites.
        for (const a of A) {
          if (a.lod !== 'bill' || !a.model || !a.model.material) continue;
          const fd = Math.min(1, Math.max(0, (cam.position.distanceTo(a.model.position) - 190) / 130));
          const k = 0.7 * fd;
          a.model.material.color.setRGB(1 - 0.19 * k, 1 - 0.11 * k, 1 - 0.07 * k);
          a.model.material.opacity = 1 - 0.4 * fd;
        }
      }
    }
    // Blob shadows: write one disc under each visible animal (single instanced draw call).
    if (!this._blobs) this._initBlobs();
    {
      const im = this._blobs; let bi = 0;
      for (const a of this.animals) {
        if (bi >= 320) break;
        if (!a.model || a.model.visible === false) continue;
        const p = a.model.position;
        this._blobP.set(p.x, t.heightAtWorld(p.x, p.z) + 0.06, p.z);
        const sz = (SPECIES[a.species] ? SPECIES[a.species].size : 1) * 1.7 * (0.55 + (a.maturity != null ? a.maturity : 1) * 0.45);
        this._blobSc.set(sz, 1, sz);
        this._blobM.compose(this._blobP, this._blobQ, this._blobSc);
        im.setMatrixAt(bi, this._blobM); bi++;
      }
      im.count = bi; im.instanceMatrix.needsUpdate = true;
    }
    for (const a of this.animals) {
      if (!a.model || a.model.visible === false) continue;   // hidden (batched) animals don't move/draw
      if (!a.target || a.model.position.distanceTo(a.target) < 0.5) {
        if (a.escaped) {
          // roam anywhere on the map
          const wx = (Math.random() * 2 - 1) * (half - 6);
          const wz = (Math.random() * 2 - 1) * (half - 6);
          a.target = new THREE.Vector3(wx, 0, wz);
          continue;
        }
        const enc = this.build.enclosures.find((e) => e.id === a.enclosureId);
        if (!enc || enc.cells.size === 0) continue;
        const cells = [...enc.cells];
        const [cx, cz] = cells[Math.floor(Math.random() * cells.length)].split(',').map(Number);
        const wx = (cx + Math.random()) * t.cellSize - half;
        const wz = (cz + Math.random()) * t.cellSize - half;
        a.target = new THREE.Vector3(wx, 0, wz);
        continue;
      }
      const dir = a.target.clone().sub(a.model.position); dir.y = 0;
      const dist = dir.length();
      dir.normalize();
      const speedMul = store.get().speed;
      const step = Math.min(dist, a.speed * dt * speedMul);
      if (step > 0.001) {
        a.model.position.addScaledVector(dir, step);
        a.model.position.y = t.heightAtWorld(a.model.position.x, a.model.position.z);
        const yaw = Math.atan2(dir.x, dir.z);
        a.model.rotation.y = yaw;
      }
      // Petting hop animation + temporary heart
      if (a.hopT != null) {
        a.hopT += dt * 2.2;
        if (a.hopT >= 1) { a.hopT = null; }
        else a.model.position.y += Math.sin(a.hopT * Math.PI) * 1.1;
      }
      if (a.emoteTimer != null) {
        a.emoteTimer -= dt;
        if (a.emoteTimer <= 0) { a.emoteTimer = null; if (a.wellbeing < 75 && a.wellbeing >= 40) a.emote.visible = false; }
      }
    }
    // Apply worker results in small chunks (4 animals/frame) — no new-day hitch
    if (this._aq) {
      const q = this._aq;
      for (let i = 0; i < 4 && q.idx < q.res.results.length; i++, q.idx++) this._applyOne(q.res.results[q.idx]);
      if (q.idx >= q.res.results.length) { this._aq = null; this._reflowRender(); bus.emit('animals:changed', this.animals.length); this._recountSick(); }
    }
  }

  // Managing the YOUNG generation as a choice. When a species has young beyond the smallest
  // “maintain” target, offer a dialog with 4 options: keep 2♂/2♀, 4♂/3♀, 8♂/6♀, or keep all
  // (100% breeding). Surplus young are sold. Never acts on its own.
  _cullingAdvice() {
    if (store.get().mandateOffer) return;   // mandate popup takes priority — don't stack dialogs
    this._cullDecline = this._cullDecline || {};
    this._cullPending = this._cullPending || {};
    const by = {};
    for (const a of this.animals) {
      if (a.escaped || a.maturity < 1) continue;   // ADULTS only — young are always kept
      const c = by[a.species] || (by[a.species] = { M: [], F: [] });
      (a.sex === 'm' ? c.M : c.F).push(a);
    }
    for (const sp of Object.keys(by)) {
      const c = by[sp];
      if (c.M.length <= 2 && c.F.length <= 2) continue; // nothing beyond 'maintain'
      if (this._cullPending[sp]) continue;
      const total = c.M.length + c.F.length;
      const declinedAt = this._cullDecline[sp];
      if (declinedAt != null && total < declinedAt + 4) continue; // re-ask only at a higher threshold
      const mk = (label, keepM, keepF) => {
        const sell = [...c.M.slice(keepM), ...c.F.slice(keepF)];
        return { label, keepM, keepF, sell: sell.length, earned: sell.reduce((s, a) => s + saleValue(a), 0) };
      };
      const options = [mk('Maintain', 2, 2), mk('Grow', 4, 3), mk('Breeding program', 8, 6)].filter((o) => o.sell > 0);
      if (!options.length) continue;
      this._cullPending[sp] = { species: sp, label: SPECIES[sp].label, am: c.M.length, af: c.F.length, options, diversity: bloodlinePct(sp), price: SPECIES[sp].price, offspring: Math.round(SPECIES[sp].price * 0.9 * bloodlineFactor(sp)), resetNet: [...c.M, ...c.F].reduce((s, a) => s + saleValue(a), 0) - SPECIES[sp].price * 4 };
      this._syncCullOffers();
    }
  }
  _syncCullOffers() { store.set({ cullOffers: Object.values(this._cullPending || {}) }); }
  chooseCull(sp, keepM, keepF) {
    const M = this.animals.filter((a) => a.species === sp && a.sex === 'm' && a.maturity >= 1 && !a.escaped);
    const F = this.animals.filter((a) => a.species === sp && a.sex === 'f' && a.maturity >= 1 && !a.escaped);
    const list = [...M.slice(keepM), ...F.slice(keepF)];
    let earned = 0;
    for (const a of list) { earned += saleValue(a); if (a.model) this.group.remove(a.model); const i = this.animals.indexOf(a); if (i >= 0) this.animals.splice(i, 1); }
    if (earned) store.set({ money: store.get().money + earned });
    this._reflowRender();
    if (this._cullPending) delete this._cullPending[sp];
    this._cullDecline[sp] = keepM + keepF;
    this._syncCullOffers();
    if (list.length) { pushNews(`Sold ${list.length} adult ${SPECIES[sp].label.toLowerCase()}${list.length > 1 ? 's' : ''} for $${earned.toLocaleString('en-US')} — kept your chosen breeding herd (young stay).`, 'success'); bus.emit('animals:changed', this.animals.length); }
  }
  declineCull(sp) { // “Keep” — 100% breeding, sell nothing; re-ask only after the herd grows further
    const total = this.animals.filter((a) => a.species === sp && a.maturity >= 1 && !a.escaped).length;
    this._cullDecline = this._cullDecline || {}; this._cullDecline[sp] = total;
    if (this._cullPending) delete this._cullPending[sp];
    this._syncCullOffers();
  }
  // Zoo-wide bloodline purity feeds prestige (and thus visitor draw). Fresh, diverse herds
  // slowly raise prestige; heavily inbred collections slowly erode it.
  _bloodlinePrestige() {
    const species = [...new Set(this.animals.filter((a) => !a.escaped).map((a) => a.species))];
    if (!species.length) return;
    const avg = species.reduce((s, sp) => s + bloodlinePct(sp) / 100, 0) / species.length; // 0..1
    store.set({ geneticQuality: Math.round(avg * 100) });
    const delta = Math.round((avg - 0.6) * 3); // +~1 at full purity, negative below 0.6
    if (delta !== 0) store.set({ prestige: Math.max(0, store.get().prestige + delta) });
    if (delta > 0 && Math.random() < 0.12) pushNews("Strong, diverse bloodlines are lifting your zoo's prestige — visitors are impressed.", 'success');
    else if (delta < 0 && Math.random() < 0.2) pushNews('Inbreeding is dulling your collection — prestige is slipping. Bring in fresh blood.', 'info');
  }

  buyNewBlood(sp) {
    const host = this.animals.find((a) => a.species === sp && !a.escaped);
    const enc = host ? this.build.enclosures.find((e) => e.id === host.enclosureId) : null;
    if (!enc || !enc.cells.size) { bus.emit('ui:toast', { text: 'No enclosure for this species.', kind: 'error' }); return; }
    const cost = SPECIES[sp].price * 4;                 // 2 fresh males + 2 fresh females
    const adults = this.animals.filter((a) => a.species === sp && a.maturity >= 1 && !a.escaped);
    const earned = adults.reduce((s, a) => s + saleValue(a), 0);
    if (store.get().money + earned < cost) { bus.emit('ui:toast', { text: 'Not enough cash for new blood!', kind: 'error' }); return; }
    for (const a of adults) { if (a.model) this.group.remove(a.model); const i = this.animals.indexOf(a); if (i >= 0) this.animals.splice(i, 1); } // sell ALL adults
    const cells = [...enc.cells];
    const rndCell = () => cells[Math.floor(Math.random() * cells.length)].split(',').map(Number);
    for (const sex of ['m', 'm', 'f', 'f']) { const [cx, cz] = rndCell(); const a = this.spawn(sp, enc, cx, cz, 1); a.sex = sex; } // 2♂/2♀ fresh, 100% new genetics
    bloodlineReset(sp);
    store.set({ money: store.get().money + earned - cost });
    if (this._cullPending) delete this._cullPending[sp];
    if (this._cullDecline) delete this._cullDecline[sp];
    this._reflowRender();
    this._syncCullOffers();
    const net = earned - cost;
    pushNews(`New blood: sold ${adults.length} adult ${SPECIES[sp].label.toLowerCase()}${adults.length !== 1 ? 's' : ''}, imported 2♂/2♀ fresh stock. Net ${net >= 0 ? '+$' + net.toLocaleString('en-US') : '-$' + Math.abs(net).toLocaleString('en-US')}, bloodline 100%.`, 'success');
    bus.emit('ui:toast', { text: `🧬 ${SPECIES[sp].label} herd reset · bloodline 100%`, kind: 'success' });
    bus.emit('animals:changed', this.animals.length);
  }

  // AI zoos periodically commission animals of a given bloodline purity — recurring income
  // from a well-kept breeding line. Offer surfaces as a dialog; fulfil by shipping adults.
  _contractTick() {
    if (this._contract) return;                      // one open offer at a time
    this._contractCd = (this._contractCd || 0) - 1;
    if (this._contractCd > 0) return;
    this._contractCd = 12 + Math.floor(Math.random() * 8); // ~2-3 weeks between offers
    // eligible: species with >=2 adults to spare beyond a 2/2 core
    const by = {};
    for (const a of this.animals) {
      if (a.escaped || a.maturity < 1) continue;
      (by[a.species] = by[a.species] || []).push(a);
    }
    const pool = Object.keys(by).filter((sp) => by[sp].length >= 4);
    if (!pool.length) return;
    const sp = pool[(Math.random() * pool.length) | 0];
    const need = 2;
    const purity = bloodlinePct(sp);
    const reqPurity = Math.max(40, Math.min(purity, 50 + Math.floor(Math.random() * 40))); // achievable
    const premium = 1.6 + (reqPurity / 100) * 0.8;   // purer contracts pay more
    const reward = Math.round(SPECIES[sp].price * need * premium);
    this._contract = { species: sp, label: SPECIES[sp].label, need, reqPurity, reward, zoo: CONTRACT_ZOOS[(Math.random() * CONTRACT_ZOOS.length) | 0] };
    store.set({ contractOffer: { ...this._contract, purity } });
  }
  acceptContract() {
    const c = this._contract; if (!c) return;
    if (bloodlinePct(c.species) < c.reqPurity) { bus.emit('ui:toast', { text: `Bloodline too low (${bloodlinePct(c.species)}% < ${c.reqPurity}% required).`, kind: 'error' }); return; }
    const adults = this.animals.filter((a) => a.species === c.species && a.maturity >= 1 && !a.escaped);
    if (adults.length < c.need + 2) { bus.emit('ui:toast', { text: `Keep a breeding core — need ${c.need + 2}+ adult ${c.label.toLowerCase()}s.`, kind: 'error' }); return; }
    const ship = adults.slice(0, c.need);
    for (const a of ship) { if (a.model) this.group.remove(a.model); const i = this.animals.indexOf(a); if (i >= 0) this.animals.splice(i, 1); }
    bus.emit('economy:track', { cat: 'contracts', amt: c.reward });
    store.set({ money: store.get().money + c.reward, prestige: store.get().prestige + 3 });
    this._reflowRender();
    pushNews(`Fulfilled ${c.zoo}'s contract: shipped ${c.need} ${c.label.toLowerCase()}s for $${c.reward.toLocaleString('en-US')} (+3 prestige).`, 'success');
    bus.emit('ui:celebrate', { kind: 'mission' });
    bus.emit('animals:changed', this.animals.length);
    this._contract = null; store.set({ contractOffer: null });
  }

  dailyTick() {
    // Age tracking (drives sale value: young animals are worth the most)
    this.animals.forEach((x) => { x.ageDays = (x.ageDays || 0) + 1; });
    this._mandateCheck();
    this._cullingAdvice();
    this._bloodlinePrestige();
    this._contractTick();
    // Ship all daily math to the sim worker (second CPU core).
    const byEnclosure = {};
    const mates = [];
    this.animals.forEach((a) => {
      const k = a.enclosureId + ':' + groupOf(a.species);
      byEnclosure[k] = (byEnclosure[k] || 0) + 1;
      if (a.sex === 'm' && a.maturity >= 1 && !mates.includes(k)) mates.push(k);
    });
    const encSizes = {};
    this.build.enclosures.forEach((e) => { encSizes[e.id] = e.cells.size; });
    const speciesTable = {};
    Object.entries(SPECIES).forEach(([k, sp]) => {
      speciesTable[k] = { foodPerDay: sp.foodPerDay, spacePerAnimal: sp.spacePerAnimal, socialMin: sp.socialMin, gestationDays: sp.gestationDays, breed: groupOf(k) };
    });
    const payload = {
      type: 'animalsDaily',
      species: speciesTable,
      keepers: this.economy ? this.economy.staff.keeper : 0,
      keeperBonus: (this.economy ? this.economy.keeperBonus(this.animals.length) : 0) + (this.research ? this.research.wellbeingBonus() : 0),
      fertMult: this.research ? this.research.fertilityMultiplier() : 1,
      enrich: (() => { const m = {}; if (this.world.props) { for (const e of this.build.enclosures) m[e.id] = this.world.props.enrichmentBonus(e.cells); } return m; })(),
      stress: this._predatorStress(),
      climatePenalty: (() => { const st = store.get(); let c = st.weather === 'heatwave' ? 15 : st.weather === 'storm' ? 12 : 0; if (st.season === 'winter') c += 8; return c; })(),
      vet: this._vetCoverage(),
      sickByEnc: (() => { const m = {}, n = {}; for (const a of this.animals) { if (a.escaped) continue; n[a.enclosureId] = (n[a.enclosureId] || 0) + 1; if (a.sick) m[a.enclosureId] = (m[a.enclosureId] || 0) + 1; } for (const k in n) m[k] = (m[k] || 0) / n[k]; return m; })(),
      encSizes,
      counts: byEnclosure,
      mates,
      animals: this.animals.map((a) => ({ id: a.id, species: a.species, sex: a.sex, maturity: a.maturity, wellbeing: a.wellbeing, escaped: !!a.escaped, pregnantDays: a.pregnantDays, encId: a.enclosureId, sick: a.sick || 0 })),
    };
    if (this._worker) this._worker.postMessage(payload);
  }

  _applyResults(res) {
    const food = Math.round(res.foodCost * (hasPerk('food') ? 0.75 : 1));
    if (food > 0) store.set({ money: Math.round(store.get().money - food) });
    this._aq = { res, idx: 0 };
    this._reflowRender();
  }

  _applyOne(r) {
    const a = this.animals.find((an) => an.id === r.id);
    if (!a) return;
    a.maturity = r.maturity;
    if (a.model) a.model.scale.setScalar(0.35 + r.maturity * 0.65);
    a.sick = r.sick || 0;
    a.wellbeing = r.wellbeing;
    if (a.emote) {
      if (a.sick) { a.emote.material = getEmotes().sick; a.emote.visible = true; }
      else if (r.wellbeing >= 75) { a.emote.material = getEmotes().happy; a.emote.visible = true; }
      else if (r.wellbeing < 40) { a.emote.material = getEmotes().sad; a.emote.visible = true; }
      else a.emote.visible = false;
    }
    const sp = SPECIES[a.species];
    if (r.died) {
      this._removeModel(a);
      const i = this.animals.indexOf(a); if (i >= 0) this.animals.splice(i, 1);
      pushNews(`${a.name ? a.name + ' the ' + sp.label.toLowerCase() : 'A ' + sp.label.toLowerCase()} died of illness. Build a Vet Clinic nearby to treat sick animals.`, 'error');
      store.set({ reputation: Math.max(5, store.get().reputation - 2) });
      this._reflowRender();
      return;
    }
    if (r.gotSick) pushNews(`${a.name ? a.name + ' the ' + sp.label.toLowerCase() : 'A ' + sp.label.toLowerCase()} has fallen ill. A Vet Clinic nearby speeds recovery.`, 'error');
    if (r.recaptured && a.escaped) {
      a.escaped = false;
      const enc = this.build.enclosures.find((e) => e.id === a.enclosureId) || this.build.enclosures[0];
      if (enc && enc.cells.size) {
        const [cx, cz] = [...enc.cells][0].split(',').map(Number);
        const t = this.world.terrain;
        const half = t.worldSize / 2;
        if (a.model) a.model.position.set((cx + 0.5) * t.cellSize - half, 0, (cz + 0.5) * t.cellSize - half);
        a.target = null;
        pushNews(`${a.name ? a.name + ' the ' + sp.label.toLowerCase() : 'The escaped ' + sp.label.toLowerCase()} has been safely recaptured.`, 'success');
        store.set({ reputation: Math.min(100, store.get().reputation + 1) });
      }
    } else if (r.escapedNow && !a.escaped) {
      a.escaped = true;
      pushNews(`ESCAPE! ${a.name ? a.name + ' the ' + sp.label.toLowerCase() : 'An unhappy ' + sp.label.toLowerCase()} broke out of its enclosure!`, 'error');
      store.set({
        reputation: Math.max(5, store.get().reputation - 3),
        prestige: Math.max(0, store.get().prestige - 5),
      });
    } else {
      a.escaped = r.escaped;
    }
    a.pregnantDays = r.pregnantDays;
    if (r.birth) {
      const enc = this.build.enclosures.find((e) => e.id === a.enclosureId);
      if (enc) {
        const t = this.world.terrain;
        const half = t.worldSize / 2;
        const bp = a.model ? a.model.position : this._spawnPos(a);
        const cx = Math.floor((bp.x + half) / t.cellSize);
        const cz = Math.floor((bp.z + half) / t.cellSize);
        // Paired groups (cattle/cervid): offspring sex picks the male or female species.
        const grp = BREED_OF[a.species];
        const babySpecies = grp ? BREED_GROUPS[grp][Math.random() < 0.5 ? 'm' : 'f'] : a.species;
        const morph = rollMorph();
        this.spawn(babySpecies, enc, cx, cz, 0.1, morph);
        bloodlineBirth(babySpecies);
        if (morph) {
          pushNews(`RARE BIRTH! A ${MORPHS[morph].label} ${SPECIES[babySpecies].label.toLowerCase()} was born — a spectacular attraction!`, 'success');
          store.set({ prestige: store.get().prestige + 5 });
        } else {
          pushNews(`${a.name ? a.name : 'A ' + sp.label.toLowerCase()} had a baby! Visitors are thrilled.`, 'success');
        }
        bus.emit('ui:celebrate', { kind: 'baby' });
        store.set({ prestige: store.get().prestige + 8, reputation: Math.min(100, store.get().reputation + 2) });
      }
    }
  }
}
